<script lang="ts">
    import { _ } from "svelte-i18n"
    import { kirbyFetch } from "../../../api/kirby/api"
    import FooterLink from "./FooterLink.svelte"
    import { lang } from "../../../store"
    import { mail, socialMedia } from "../../../configs/config"

    let essentialNav: {
        text: string
        link: string
    }[] = $state([])
    let serviceNav: {
        text: string
        link: string
    }[] = $state([])

    type FooterNavPage = {
        uuid: string
        url: string
    }

    function localizeNavLinks(items: { text: string; link: string }[], pages: FooterNavPage[], currentLang: string) {
        return items.map((item) => {
            const pageUrl = pages.find((page) => page.uuid === item.link)?.url
            if (!pageUrl) return item
            return {
                ...item,
                link: `/${currentLang}/` + pageUrl.split(`/${currentLang}/`).pop(),
            }
        })
    }

    async function loadFooterNavs(currentLang: string) {
        const essential = await kirbyFetch<Site["essential_nav"]>({
            query: "site.essential_nav.toObject",
        })
        const essentialPages =
            (await kirbyFetch<FooterNavPage[]>({
                query: `site.index.filterBy('Uuid', 'in', ['${essential.map((el) => el.link).join("','")}'])`,
                select: {
                    uuid: true,
                    url: true,
                },
            })) || []
        essentialNav = localizeNavLinks(essential, essentialPages, currentLang)

        const service = await kirbyFetch<Site["service_nav"]>({
            query: "site.service_nav.toObject",
        })
        const servicePages =
            (await kirbyFetch<FooterNavPage[]>({
                query: `site.index.filterBy('Uuid', 'in', ['${service.map((el) => el.link).join("','")}'])`,
                select: {
                    uuid: true,
                    url: true,
                },
            })) || []
        serviceNav = localizeNavLinks(service, servicePages, currentLang)
    }

    $effect(() => {
        const currentLang = String($lang || "")
        if (!currentLang) return
        void loadFooterNavs(currentLang)
    })

    // SSR: $effect() läuft nicht im Server-Kontext, daher manuell laden
    if (typeof window === "undefined") {
        void loadFooterNavs(String($lang || "de"))
    }
</script>

<div class="white-bar"></div>
<footer id="footer">
    <div class="wrapper">
        <div class="section" id="s1">
            <h5>{$_("footer.essential")}</h5>
            <div class="page-refs">
                {#each essentialNav as nav}
                    <FooterLink page={nav.link} text={nav.text} />
                {/each}
            </div>
        </div>
        <div class="section" id="s2">
            <h5>{$_("footer.address")}</h5>
            <div>
                <p>
                    {$_("general.sfu")}<br />
                    {$_("general.pariser")}<br />
                    {$_("general.berlin")}<br />
                    {$_("general.country")}
                </p>
                <br />
                <div>
                    <span>{$_("footer.tel")}:</span> <a href="tel:+4930226052910">+ 49 (0) 30 / 22 60 52 91 0</a>
                </div>
                <div>
                    <span>{$_("footer.telfax")}:</span> <a href="tel:+4930226052929">+ 49 (0) 30 / 22 60 52 92 9</a>
                </div>
                <br />
                <div>
                    <span>{$_("footer.email")}:</span>
                    <a href="mailto:{mail}">{mail}</a>
                </div>
            </div>
            <div class="socials">
                <a href={socialMedia.x} target="_blank" aria-label="X">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M20 3L4 21M16.82 20.768L3.753 3.968C3.68413 3.87931 3.64152 3.77307 3.63004 3.66137C3.61855 3.54967 3.63864 3.43698 3.68803 3.33613C3.73741 3.23528 3.8141 3.15032 3.90938 3.09089C4.00466 3.03147 4.11471 2.99998 4.227 3H6.707C6.79829 3.00014 6.88836 3.0211 6.97032 3.06131C7.05229 3.10151 7.124 3.15989 7.18 3.232L20.247 20.032C20.3159 20.1207 20.3585 20.2269 20.37 20.3386C20.3814 20.4503 20.3614 20.563 20.312 20.6639C20.2626 20.7647 20.1859 20.8497 20.0906 20.9091C19.9953 20.9685 19.8853 21 19.773 21H17.293C17.2017 20.9999 17.1116 20.9789 17.0297 20.9387C16.9477 20.8985 16.876 20.8401 16.82 20.768Z"
                            stroke="white"
                            stroke-width="1.5"
                            stroke-linecap="round"
                        ></path>
                    </svg>
                </a>

                <a href={socialMedia.linkedIn} target="_blank" aria-label="Linked In">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <path
                            d="M7 17V10M11 17V13.75M11 13.75V10M11 13.75C11 10 17 10 17 13.75V17M7 7.01L7.01 6.999M21 8V16C21 17.3261 20.4732 18.5979 19.5355 19.5355C18.5979 20.4732 17.3261 21 16 21H8C6.67392 21 5.40215 20.4732 4.46447 19.5355C3.52678 18.5979 3 17.3261 3 16V8C3 6.67392 3.52678 5.40215 4.46447 4.46447C5.40215 3.52678 6.67392 3 8 3H16C17.3261 3 18.5979 3.52678 19.5355 4.46447C20.4732 5.40215 21 6.67392 21 8Z"
                            stroke="white"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        ></path>
                    </svg>
                </a>
                <a href={socialMedia.instagram} target="_blank" aria-label="Instagram">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M12 16C13.0609 16 14.0783 15.5786 14.8284 14.8284C15.5786 14.0783 16 13.0609 16 12C16 10.9391 15.5786 9.92172 14.8284 9.17157C14.0783 8.42143 13.0609 8 12 8C10.9391 8 9.92172 8.42143 9.17157 9.17157C8.42143 9.92172 8 10.9391 8 12C8 13.0609 8.42143 14.0783 9.17157 14.8284C9.92172 15.5786 10.9391 16 12 16Z"
                            stroke="white"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        ></path>
                        <path
                            d="M3 16V8C3 6.67392 3.52678 5.40215 4.46447 4.46447C5.40215 3.52678 6.67392 3 8 3H16C17.3261 3 18.5979 3.52678 19.5355 4.46447C20.4732 5.40215 21 6.67392 21 8V16C21 17.3261 20.4732 18.5979 19.5355 19.5355C18.5979 20.4732 17.3261 21 16 21H8C6.67392 21 5.40215 20.4732 4.46447 19.5355C3.52678 18.5979 3 17.3261 3 16Z"
                            stroke="white"
                            stroke-width="1.5"
                        ></path>
                        <path
                            d="M17.5 6.50999L17.51 6.49899"
                            stroke="white"
                            stroke-width="1.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                        ></path>
                    </svg>
                </a>
            </div>
        </div>
        <div class="section" id="s3">
            <h5>{$_("footer.service")}</h5>
            <div class="page-refs">
                {#each serviceNav as nav}
                    <FooterLink page={nav.link} text={nav.text} />
                {/each}
            </div>
        </div>
    </div>
</footer>

<style lang="less" global>
    @import "../../../css/variables.less";
    .white-bar {
        height: 6rem !important;
        padding-top: 2.4rem;
        margin-top: -10px;
        background-color: var(--bg-100);
        z-index: 3;
        position: relative;
    }

    footer#footer {
        z-index: 100;
        position: relative;
        padding: 0px 2.4rem;
        margin: -1rem 0px;

        background-color: var(--bg-400);
        border-radius: 1rem 1rem 0px 0px;
        * {
            color: var(--bright-100) !important;
        }
        display: flex;
        justify-content: center;
        @media @max-mobile {
            padding: 0px;
        }
        .wrapper {
            display: flex;
            max-width: var(--full-hd-max-width);
            width: 100%;
            flex-direction: column;
            @media @desktop_large {
                display: grid;
                grid-template-columns: repeat(3, 1fr);
            }
            .section {
                * {
                    font-size: 1rem;
                }
                flex-grow: 1;
                &:first-child {
                    padding-left: 0px;
                }
                &:last-child {
                    padding-right: 0px;
                    border-right: none;
                }
                display: flex;
                flex-direction: column;
                border-right: none;
                border-bottom: 1px solid var(--stroke-100);
                padding: 1.2rem !important;
                gap: 1.2rem;
                @media @desktop_large {
                    border-right: 1px solid var(--stroke-100);
                    border-bottom: none;
                    padding: 2.4rem !important;
                    gap: 2.4rem;
                }
                h5 {
                    font-size: 1rem;
                    font-weight: 700;
                    line-height: 145%;
                    font-weight: 700;
                }
                .page-refs {
                    display: flex;
                    flex-direction: column;
                }
                .socials {
                    display: flex;
                    gap: 0.6rem;
                    a {
                        svg {
                            path {
                                stroke: white;
                                transition: 0.3s stroke ease-in-out;
                                fill: unset !important;
                                transition: none !important;
                            }
                        }
                        &:hover {
                            path {
                                stroke: var(--brand-100);
                            }
                        }
                    }
                }
            }
        }
    }
</style>
